import { useState, useEffect } from "react";
import {
  Layout,
  Text,
  Button,
  Modal,
  AnchorLink,
  TextArea,
  Icon,
  IconList,
} from "@kidslivesafe/sc-react";

import { CombinedReports } from "../../../features/reports/reportsSlice";
import { ReportList } from "../../../features/reports/reportTypes";
import {
  useRemoveReportNoteDispatch,
  useUpdateReportNoteDispatch,
} from "../../../hooks/notesHook";
import {
  NoteValidationResult,
  useNoteValidation,
} from "../../../hooks/noteValidationHook";

export type NoteModalMode = "remove-note" | "add-note" | "edit-note";

export interface NoteModalProps {
  modalMode: NoteModalMode;
  setModalMode: (mode: NoteModalMode) => void;
  displayModal: boolean;
  setDisplayModal: (display: boolean) => void;
  report?: CombinedReports;
  list?: ReportList;
  noteId?: number;
  noteText: string;
}

const NoteModal = ({
  modalMode,
  setModalMode,
  displayModal = true,
  setDisplayModal,
  noteId,
  noteText,
}: NoteModalProps) => {
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [updatedNoteText, setUpdatedNoteText] = useState<string>("");
  const { getNoteValidation } = useNoteValidation();

  const initialMode = modalMode;

  const removeNoteFromReport = useRemoveReportNoteDispatch();
  const updateReportNote = useUpdateReportNoteDispatch();
  const [noteTextError, setNoteTextError] = useState<boolean>(false);
  const [disableNoteSave, setDisableNoteSave] = useState<boolean>(true);
  const [noteErrorText, setNoteErrorText] = useState<string>("");

  const applyNoteValidation = (validation: NoteValidationResult) => {
    setDisableNoteSave(validation.disableNoteSave);
    setNoteTextError(validation.noteTextError);
    setNoteErrorText(validation.noteErrorText);
  };

  useEffect(() => {
    applyNoteValidation(getNoteValidation(updatedNoteText, "typing", true));
  }, [updatedNoteText]);

  useEffect(() => {
    if (displayModal && modalMode === "edit-note") {
      setUpdatedNoteText(noteText);
    }
  }, [displayModal, modalMode, noteText]);

  const resetNoteText = () => {
    setUpdatedNoteText(noteText);
  };

  const handleDeleteNote = () => {
    if (noteId) {
      removeNoteFromReport({
        reportNoteId: noteId,
      });
      setActionCompleted(true);
      setTimeout(() => {
        setDisplayModal(false);
        setActionCompleted(false);
      }, 2000);
    }
  };

  const handleNoteTextUpdate = () => {
    const validation = getNoteValidation(updatedNoteText, "submit");
    applyNoteValidation(validation);

    if (noteId && !validation.disableNoteSave) {
      updateReportNote({
        reportNoteId: noteId,
        note: updatedNoteText,
      });
      setActionCompleted(true);
      setTimeout(() => {
        setDisplayModal(false);
        setActionCompleted(false);
      }, 2000);
    }
  };

  const renderContent = () => {
    switch (modalMode) {
      case "remove-note":
        return (
          <Layout
            type="flex"
            fillVertical
            flexProps={{
              gap: "lg",
              direction: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!actionCompleted ? (
              <>
                <Text textAlign="center" tag="h2" fontWeight="semibold">
                  Delete Note{" "}
                </Text>
                <Text textAlign="center" size="sm">
                  This action is permanent. Are you sure that you would like to
                  delete this note?
                </Text>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    alignItems: "center",
                    gap: "sm",
                  }}
                  fillHorizontal
                >
                  <Button
                    handleClick={handleDeleteNote}
                    buttonText={"Yes, Delete"}
                    size="md"
                    fill="default"
                    color="primary"
                  />
                  <AnchorLink
                    handleClick={() => {
                      setDisplayModal(false);
                    }}
                    size="xs"
                    role="button"
                  >
                    No, Cancel
                  </AnchorLink>
                </Layout>
              </>
            ) : (
              <>
                <Text textAlign="center" tag="h2" fontWeight="semibold">
                  Note Deleted
                </Text>
                <Text textAlign="center" size="sm">
                  Your note has been deleted. This message will automatically
                  close.
                </Text>
              </>
            )}
          </Layout>
        );
      case "edit-note":
        return (
          <Layout
            type="flex"
            fillHorizontal
            flexProps={{
              gap: "sm",
              direction: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text textAlign="center" tag="h2" fontWeight="semibold">
              Edit Note{" "}
            </Text>
            <Text textAlign="center" size="sm">
              Your edits will replace the existing note.
            </Text>

            <TextArea
              id="updateNoteText"
              placeholder="Add updated note text here"
              handleChange={input => {
                setUpdatedNoteText(input);
              }}
              value={updatedNoteText}
              characterLimit={500}
              error={noteTextError}
              errorText={noteErrorText}
            />
            <Layout
              type="flex"
              fillHorizontal
              flexProps={{
                gap: "xxs",
                direction: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text textAlign="center" size="sm">
                Click 'Update' to save your changes, or exit without saving to
                discard them.
              </Text>
            </Layout>
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                alignItems: "center",
                gap: "md",
              }}
              fillHorizontal
            >
              <Button
                handleClick={() => {
                  updatedNoteText.length === 0
                    ? handleDeleteNote()
                    : handleNoteTextUpdate();
                }}
                buttonText={
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xxs", alignItems: "center" }}
                  >
                    {actionCompleted ? (
                      <Icon name={IconList.IconYes} size={16} />
                    ) : null}
                    <Text>
                      {updatedNoteText.length === 0
                        ? "Delete Note"
                        : actionCompleted
                          ? "Changes Saved"
                          : "Update"}
                    </Text>
                  </Layout>
                }
                size="md"
                fill="default"
                color={
                  actionCompleted
                    ? "success"
                    : disableNoteSave
                      ? "disabled"
                      : "primary"
                }
              />
              <AnchorLink
                handleClick={() => {
                  setDisplayModal(false);
                  resetNoteText();
                }}
                size="xs"
                role="button"
                color={actionCompleted ? "gray" : "primary"}
                disabled={actionCompleted}
              >
                No, Cancel
              </AnchorLink>
            </Layout>
          </Layout>
        );
    }
  };

  return (
    <Modal
      open={displayModal}
      setOpen={() => {
        setDisplayModal(false);
        setActionCompleted(false); // reset all local state on close
        setModalMode(initialMode);
        setUpdatedNoteText(noteText);
      }}
      size={modalMode === "remove-note" ? "base" : "fit-content"}
      centerContentVertically={true}
      scrollable={false}
    >
      {renderContent()}
    </Modal>
  );
};

export default NoteModal;
